import React, { useState, useEffect, useCallback, useContext } from 'react'
import ReactDOM from 'react-dom'
import styled from 'styled-components'
import { ResponsiveSVG } from 'ui/acro'
import Text from 'ui/acro/typography/Text'
import ThreeDots from '../../images/latest/three-dots.svg'
import Schedule from '../../../assets/images/schedule.svg'
import PlaylistNew from '../../../assets/images/PlaylistNew.svg'
import ThumbsUp from '../../../assets/images/thumbsUp.svg'
import ThumbsUpSelected from '../../../assets/images/thumbsUpSelected.svg'
import HeartStroke from '../../../assets/images/heart.svg'
import HeartFilled from '../../../assets/images/heartFilled.svg'
import DuplicateIcon from '../../images/latest/duplicate_icon.svg'
import ShareIconSmall from '../../images/latest/share_icon.svg'
import closeSVG from '../../../assets/images/X.svg'
import { ClassViewContext } from 'components_v2/page_context'

const MOBILE_MQ = '(max-width: 767px)'

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false
    return window.matchMedia(MOBILE_MQ).matches
  })
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined
    const mq = window.matchMedia(MOBILE_MQ)
    const update = () => setIsMobile(mq.matches)
    update()
    try {
      mq.addEventListener('change', update)
    } catch (e) {
      mq.addListener(update)
    }
    return () => {
      try {
        mq.removeEventListener('change', update)
      } catch (e) {
        mq.removeListener(update)
      }
    }
  }, [])
  return isMobile
}

function triggerPdpAction(action) {
  if (action === 'like') {
    document?.getElementById('like-button')?.click()
    return
  }
  if (action === 'save') {
    document?.getElementById('class-favourite-button')?.click()
    return
  }
  if (action === 'schedule') {
    document?.getElementById('schedule-button')?.click()
    return
  }

  const root = document.querySelector('.pdp-class-actions')
  if (!root) return

  if (action === 'share') {
    root
      .querySelector('[data-pdp-action="share"] [data-pdp-share-trigger]')
      ?.click()
    return
  }

  const host = root.querySelector(`[data-pdp-action="${action}"]`)
  if (!host) return

  const inner =
    host.querySelector('[class*="Container"]') || host.firstElementChild
  if (inner) {
    inner.dispatchEvent(
      new MouseEvent('click', { bubbles: true, cancelable: true, view: window })
    )
    return
  }
  host.click()
}

const MoreTrigger = styled.button`
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  color: #000;
  width: 62px;
  flex-shrink: 0;
`

const MoreLabel = styled(Text)({
  fontSize: ['14px'],
  fontFamily: 'Proxima-nova',
  fontWeight: 400,
  lineHeight: '18px',
  letterSpacing: '0.308px'
})

const DrawerBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 10040;
  background: rgba(0, 0, 0, 0.5);
`

const DrawerPanel = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10041;
  max-height: 80vh;
  background: #fff;
  border-radius: 8px 8px 0 0;
  overflow: auto;
  padding-bottom: env(safe-area-inset-bottom, 16px);
`

const DrawerHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 38px 16px 16px;
`

const DrawerTitle = styled.p`
  margin: 0;
  font-family: 'Proxima-nova', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.352px;
  color: #000;
  padding-right: 16px;
`

const CloseBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  flex-shrink: 0;
  line-height: 0;
`

const DrawerList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 0 16px 32px;
`

const DrawerRow = styled.button`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
  width: 100%;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  text-align: left;
  font-family: 'Proxima-nova', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
  letter-spacing: 0.308px;
  color: #000;
`

const RowIcon = styled.span`
  display: flex;
  width: 24px;
  height: 24px;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

const MoreWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

function PlanEntryMoreDrawer({ open, onClose, classTitle, saved }) {
  const { classState } = useContext(ClassViewContext)
  const likeCount = classState?.likes || 0
  const userLiked = classState?.userLiked || 0

  useEffect(() => {
    if (!open) return undefined
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKey = (e) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = prev
      document.removeEventListener('keydown', onKey)
    }
  }, [open, onClose])

  const run = useCallback(
    (fn) => {
      fn()
      onClose()
    },
    [onClose]
  )

  if (!open) return null

  const node = (
    <>
      <DrawerBackdrop role='presentation' onClick={onClose} />
      <DrawerPanel
        role='dialog'
        aria-modal='true'
        aria-labelledby='pdp-more-title'
      >
        <DrawerHeader>
          <DrawerTitle id='pdp-more-title'>{classTitle}</DrawerTitle>
          <CloseBtn type='button' onClick={onClose} aria-label='Close'>
            <ResponsiveSVG SVG={closeSVG} width='20px' height='20px' />
          </CloseBtn>
        </DrawerHeader>
        <DrawerList>
          <DrawerRow
            type='button'
            aria-pressed={saved}
            onClick={() => run(() => triggerPdpAction('save'))}
          >
            <RowIcon>
              <ResponsiveSVG
                SVG={saved ? HeartFilled : HeartStroke}
                width='24px'
                height='24px'
              />
            </RowIcon>
            {saved ? 'Saved' : 'Save'}
          </DrawerRow>
          <DrawerRow
            type='button'
            onClick={() => run(() => triggerPdpAction('schedule'))}
          >
            <RowIcon>
              <ResponsiveSVG SVG={Schedule} width='24px' height='24px' />
            </RowIcon>
            Add To Your Calendar
          </DrawerRow>
          <DrawerRow
            type='button'
            onClick={() => run(() => triggerPdpAction('playlist'))}
          >
            <RowIcon>
              <ResponsiveSVG SVG={PlaylistNew} width='24px' height='24px' />
            </RowIcon>
            Add to Playlist
          </DrawerRow>
          <DrawerRow
            type='button'
            aria-pressed={userLiked}
            aria-label={`${likeCount} likes${
              userLiked ? ', liked by you' : ''
            }. Press to toggle.`}
            onClick={() => run(() => triggerPdpAction('like'))}
          >
            <RowIcon>
              <ResponsiveSVG
                SVG={userLiked ? ThumbsUpSelected : ThumbsUp}
                width='24px'
                height='24px'
              />
            </RowIcon>
            {likeCount}
          </DrawerRow>
          <DrawerRow
            type='button'
            onClick={() => {
              onClose()
              if (typeof window !== 'undefined' && window.scrollToComment) {
                window.scrollToComment()
              }
            }}
          >
            <RowIcon>
              <ResponsiveSVG SVG={DuplicateIcon} width='24px' height='24px' />
            </RowIcon>
            Leave a Comment
          </DrawerRow>
          <DrawerRow
            type='button'
            onClick={() => {
              onClose()
              window.requestAnimationFrame(() => {
                const bar = document.querySelector('.pdp-class-actions')
                const sheetLaunch = bar?.querySelector(
                  '[data-class-pdp-share-presentation="sheet"][data-class-pdp-share-open]'
                )
                if (sheetLaunch) {
                  sheetLaunch.click()
                  return
                }
                triggerPdpAction('share')
              })
            }}
          >
            <RowIcon>
              <ResponsiveSVG SVG={ShareIconSmall} width='24px' height='24px' />
            </RowIcon>
            Share
          </DrawerRow>
        </DrawerList>
      </DrawerPanel>
    </>
  )

  return ReactDOM.createPortal(node, document.body)
}

/** More + drawer: mobile / small screens only (max-width 767px). Tablet/desktop use inline actions; drawer open is gated by useIsMobile. */
const ClassPageActions = ({ saved, ...props }) => {
  const classTitle =
    props.classTitle ??
    props.classEntryTitle ??
    props.class_entry_title ??
    'Class'
  const [moreOpen, setMoreOpen] = useState(false)
  const isMobile = useIsMobile()

  return (
    <MoreWrap>
      <MoreTrigger
        type='button'
        className='pdp-class-pdp-more-mobile'
        aria-label='More actions'
        onClick={() => setMoreOpen(true)}
      >
        <ResponsiveSVG SVG={ThreeDots} width='24px' height='24px' />
        <MoreLabel>More</MoreLabel>
      </MoreTrigger>
      <PlanEntryMoreDrawer
        saved={saved}
        open={moreOpen && isMobile}
        onClose={() => setMoreOpen(false)}
        classTitle={classTitle}
      />
    </MoreWrap>
  )
}

export default ClassPageActions
